import { Component, createRef } from 'preact';
import { motion } from 'framer-motion';
import {
  GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, PANEL_BG, ACCENT_GOLD,
  GOLD_BTN, INPUT_STYLE, FourCorners, OrnamentalDivider,
  TEXTURED_PANEL_STYLE, SUB_PANEL_STYLE, VIGNETTE,
} from '../../lib/medievalTheme';
import { resolveAvatarUrl } from '../../utils/arena/avatarUtils';
import AvatarImage from '../ui/AvatarImage';
import { pushDismiss, removeDismiss } from '../../utils/escStack';
import { t } from '../../localization/t';

/**
 * Friend-picker modal for the "Friend Match" play tile.
 *
 * Visually matches ModeSetupDialog / ArenaDeckSelect: textured stone
 * panel, ornamental dividers, gradient-gold title, Cinzel headings.
 * The flow is single-purpose. Click an online friend to send the
 * invite, the row flips to "Invited", and the caller decides what
 * to do next (typically dismiss after one invite goes out).
 */

const HEADER_TITLE_STYLE = {
  fontSize: '1.625cqw',
  lineHeight: 1.05,
  letterSpacing: '0.04em',
  fontWeight: 800,
  background: 'linear-gradient(180deg, #f7e4b6 0%, #d4a843 60%, #a8772d 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  textShadow: '0 1px 0 rgba(0,0,0,0.4), 0 0 18px rgba(212,168,67,0.18)',
  fontFamily: "'FantaisieArtistique', cursive",
};

const SECTION_LABEL_STYLE = {
  color: `${GOLD} 0.55)`,
 fontSize: '0.625cqw',
 fontWeight: 700,
 fontFamily: "'FantaisieArtistique', cursive",
};

export default class FriendInvitePicker extends Component {
 constructor(props) {
 super(props);
 this.state = {
 invitedIds: new Set(),
 // Local filter. Narrows the friend list by case-insensitive
 // name substring. Intentionally client-side only: this picker
 // never searches the broader player directory, just filters
 // whatever friends are already in friendListData.
 query: '',
 };
 // autoFocus on the search input only fires reliably on the first
 // picker mount of the session; subsequent opens need an imperative
 // focus() to latch the smooth caret onto the field.
 this.searchInputRef = createRef();
 }

 componentDidMount() {
 this._escDismissId = pushDismiss(() => this.props.onClose?.());
 this.searchInputRef.current?.focus();
 }

 componentWillUnmount() {
 removeDismiss(this._escDismissId);
 }

 handleInvite = (friend) => {
 if (!friend.online) return;
 if (this.state.invitedIds.has(friend.id)) return;
 this.setState((s) => ({ invitedIds: new Set(s.invitedIds).add(friend.id) }));
 this.props.onInvite?.(friend.id);
 };

 renderGroupHeader(label, count, online) {
 return (
 <div className="flex items-center gap-2 px-1 py-1.5">
 <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg, transparent 0%, ${GOLD} 0.35) 100%)` }} />
        <span
          className="flex items-center gap-1.5 text-[0.625cqw] font-semibold"
          style={{ color: online ? `${GOLD} 0.7)` : TEXT_MUTED, fontFamily: "'FantaisieArtistique', cursive" }}
        >
          <span
            className={`w-1.5 h-1.5 rounded-full ${online ? 'animate-pulse' : ''}`}
            style={{ background: online ? ACCENT_GOLD : `${GOLD} 0.18)` }}
          />
          {t('{label}. {n}', { label, n: count })}
        </span>
        <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg, ${GOLD} 0.35) 0%, transparent 100%)` }} />
      </div>
    );
  }

  renderRow(friend, sorceryCards, invited) {
    const avatarUrl = resolveAvatarUrl(friend, sorceryCards);
    const disabled = !friend.online;
    return (
      <button
        key={friend.id}
        type="button"
        disabled={disabled || invited}
        onClick={() => this.handleInvite(friend)}
        className="w-full flex items-center gap-3 px-3 py-2 text-left transition-all"
        style={{
          ...SUB_PANEL_STYLE,
          opacity: disabled ? 0.45 : 1,
          marginBottom: '0.25cqw',
        }}
      >
        <div className="relative shrink-0">
          {avatarUrl ? (
            <AvatarImage
              src={avatarUrl}
              className="w-10 h-10 rounded-lg"
              style={{ border: `1px solid ${GOLD} 0.28)` }}
            />
          ) : (
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center text-sm font-medium"
              style={{ background: `${GOLD} 0.08)`, border: `1px solid ${GOLD} 0.2)`, color: TEXT_MUTED, fontFamily: "'FantaisieArtistique', cursive" }}
            >
              {(friend.name || '?')[0].toUpperCase()}
            </div>
          )}
          <div
            className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full"
            style={{
              background: friend.online ? ACCENT_GOLD : `${GOLD} 0.18)`,
              border: `2px solid #0e0a06`,
              boxShadow: friend.online ? `0 0 0.4cqw ${GOLD} 0.55)` : 'none',
            }}
          />
        </div>
        <div className="flex-1 min-w-0">
          <div
            className="text-sm font-bold truncate"
            style={{ color: TEXT_PRIMARY, fontFamily: "'FantaisieArtistique', cursive", letterSpacing: '0.02em' }}
          >
            {friend.name}
          </div>
          <div className="text-[0.625cqw] truncate" style={{ color: friend.online ? `${GOLD} 0.55)` : TEXT_MUTED }}>
            {friend.online ? (friend.customStatus || t('Online')) : t('Offline')}
          </div>
        </div>
        {invited ? (
          <span
            className="text-[0.6875cqw] px-2 py-1 rounded font-semibold"
            style={{
              color: ACCENT_GOLD,
              background: `${GOLD} 0.12)`,
              border: `1px solid ${GOLD} 0.35)`,
              fontFamily: "'FantaisieArtistique', cursive",
            }}
          >
            {t('Invited')}
          </span>
        ) : friend.online ? (
          <span
            className="text-[0.6875cqw] px-3 py-1 rounded font-semibold"
            style={{ ...GOLD_BTN, borderRadius: 'var(--radius-md)', fontFamily: "'FantaisieArtistique', cursive", letterSpacing: '0.04em' }}
          >
            {t('Invite')}
          </span>
        ) : null}
      </button>
    );
  }

  render() {
    const { friendListData, sorceryCards, onClose } = this.props;
    const { invitedIds, query } = this.state;
    const allFriends = friendListData?.friends || [];
    const needle = query.trim().toLowerCase();
    const friends = needle
      ? allFriends.filter((f) => (f.name || '').toLowerCase().includes(needle))
      : allFriends;
    // Online friends first. The only ones an invite makes sense for.
    // then offline so the user can still see who's around.
    const online = friends.filter((f) => f.online);
    const offline = friends.filter((f) => !f.online);

    return (
      <div
        className="fixed inset-0 z-[1200] flex items-center justify-center"
        style={{ background: 'rgba(0,0,0,0.78)', backdropFilter: 'blur(2px)' }}
        onClick={onClose}
      >
        <div className="fixed inset-0 pointer-events-none" style={{ background: VIGNETTE }} />

        <motion.div
          initial={{ opacity: 0, y: 12, scale: 0.985 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.99 }}
          transition={{ duration: 0.28, ease: [0.22, 0.8, 0.36, 1] }}
          className="relative w-[28cqw] flex flex-col overflow-hidden"
          style={{ ...TEXTURED_PANEL_STYLE, height: '70cqh' }}
          onClick={(e) => e.stopPropagation()}
        >
          <FourCorners knots knotSize={14} knotOpacity={0.45} />

          {/* Header. Gradient gold title + small subtitle line */}
          <div className="px-6 pt-5 pb-3 text-center shrink-0">
            <h2 style={HEADER_TITLE_STYLE}>{t('Invite a Friend')}</h2>
            <div
              className="text-[0.6875cqw] mt-1"
              style={{ color: `${GOLD} 0.5)`, fontFamily: "'FantaisieArtistique', cursive" }}
            >
              {t('Send a match invite')}
            </div>
          </div>
          <div className="px-6 shrink-0">
            <OrnamentalDivider />
          </div>

          {/* Search */}
          <div className="px-6 py-3 shrink-0">
            <div className="relative">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                style={{ width: '0.875cqw', height: '0.875cqw', color: `${GOLD} 0.35)` }}
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
              <input
                ref={this.searchInputRef}
                type="text"
                value={query}
                placeholder={t('Search your friends...')}
                className="w-full pl-9 pr-3 py-2 text-sm outline-none"
                style={{ ...INPUT_STYLE, borderRadius: 'var(--radius-md)', color: TEXT_PRIMARY }}
                onInput={(e) => this.setState({ query: e.currentTarget.value })}
                autoFocus
              />
            </div>
          </div>

          {/* List body. Scrollable, takes the remaining vertical
              space so the panel stays the same height whether the
              player has 2 friends or 200. */}
          <div className="flex-1 min-h-0 overflow-y-auto medieval-scrollbar px-4 pb-4">
            {allFriends.length === 0 ? (
              <div className="h-full flex items-center justify-center text-center px-6">
                <div>
                  <div className="text-sm" style={{ color: TEXT_BODY, fontFamily: "'FantaisieArtistique', cursive" }}>{t('No friends yet')}</div>
                  <div className="text-[0.75cqw] mt-1" style={{ color: TEXT_MUTED }}>
                    {t('Add friends from the Friends sidebar first.')}
                  </div>
                </div>
              </div>
            ) : friends.length === 0 ? (
              <div className="h-full flex items-center justify-center text-center">
                <div className="text-sm italic" style={{ color: TEXT_MUTED }}>{t('No friends match your search.')}</div>
              </div>
            ) : (
              <>
                {online.length > 0 ? (
                  <div className="mb-2">
                    {this.renderGroupHeader(t('Online'), online.length, true)}
                    <div className="flex flex-col">
                      {online.map((f) => this.renderRow(f, sorceryCards, invitedIds.has(f.id)))}
                    </div>
                  </div>
                ) : null}
                {offline.length > 0 ? (
                  <div>
                    {this.renderGroupHeader(t('Offline'), offline.length, false)}
                    <div className="flex flex-col">
                      {offline.map((f) => this.renderRow(f, sorceryCards, false))}
                    </div>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </motion.div>
      </div>
    );
  }
}

FriendInvitePicker.displayName = 'FriendInvitePicker';
